import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Portal,
  Select,
  SimpleGrid,
  Text,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

const WEEKDAYS_EN = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const WEEKDAYS_FR = ["Di", "Lu", "Ma", "Me", "Je", "Ve", "Sa"];
const WEEKDAYS_RW = ["cy", "mb", "ka", "ga", "gn", "gd", "gt"];

function getDateLocale(language) {
  if (language?.startsWith("fr")) return "fr-FR";
  if (language?.startsWith("rw")) return "rw-RW";
  return "en-US";
}

function getWeekdays(language) {
  if (language?.startsWith("fr")) return WEEKDAYS_FR;
  if (language?.startsWith("rw")) return WEEKDAYS_RW;
  return WEEKDAYS_EN;
}

function parseISODate(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}

function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date, delta) {
  return new Date(date.getFullYear(), date.getMonth() + delta, 1);
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function clampViewMonth(year, month, minDate, maxDate) {
  let view = new Date(year, month, 1);
  if (minDate) {
    const minMonth = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    if (view < minMonth) view = minMonth;
  }
  if (maxDate) {
    const maxMonth = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
    if (view > maxMonth) view = maxMonth;
  }
  return view;
}

function buildCalendarDays(viewMonth) {
  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const first = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0).getDate();
  const pad = first.getDay();
  const cells = [];

  for (let i = 0; i < pad; i += 1) cells.push(null);
  for (let day = 1; day <= lastDay; day += 1) {
    cells.push(new Date(year, month, day));
  }
  return cells;
}

const BirthDatePicker = ({ value, onChange, min, max, isInvalid = false }) => {
  const { t, i18n } = useTranslation();
  const locale = getDateLocale(i18n.language);
  const weekdays = getWeekdays(i18n.language);

  const selected = parseISODate(value);
  const minDate = parseISODate(min);
  const maxDate = parseISODate(max);

  const defaultView = selected || maxDate || new Date();
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(defaultView));
  const { isOpen, onOpen, onClose } = useDisclosure();

  const triggerBg = useColorModeValue("white", "rgba(255,255,255,0.06)");
  const triggerBorder = useColorModeValue(
    isInvalid ? "red.300" : "gray.200",
    isInvalid ? "red.400" : "whiteAlpha.200"
  );
  const triggerHoverBorder = useColorModeValue("brand.400", "brand.400");
  const triggerColor = useColorModeValue("gray.900", "whiteAlpha.900");
  const placeholderColor = useColorModeValue("gray.500", "whiteAlpha.500");
  const panelBg = useColorModeValue("#ffffff", "#161618");
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const panelShadow = useColorModeValue(
    "0 16px 48px rgba(15,23,42,0.14)",
    "0 24px 64px rgba(0,0,0,0.65), 0 0 0 1px rgba(255,255,255,0.08)"
  );
  const muted = useColorModeValue("gray.500", "whiteAlpha.550");
  const titleColor = useColorModeValue("gray.900", "white");
  const dayHoverBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const dayMuted = useColorModeValue("gray.300", "whiteAlpha.300");
  const iconBtnHover = useColorModeValue("gray.100", "whiteAlpha.100");
  const todayRing = useColorModeValue("brand.400", "brand.300");
  const selectBg = useColorModeValue("white", "rgba(255,255,255,0.06)");
  const selectBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const selectFocus = {
    borderColor: "brand.400",
    boxShadow: "0 0 0 1px var(--chakra-colors-brand-400)",
  };
  const nativeSelectOptionSx = useColorModeValue(
    {},
    {
      "& option": {
        backgroundColor: "#161618",
        color: "rgba(255, 255, 255, 0.92)",
      },
    }
  );

  const displayLabel = useMemo(() => {
    if (!selected) return null;
    return selected.toLocaleDateString(locale, {
      weekday: "short",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, [selected, locale]);

  const viewYear = viewMonth.getFullYear();
  const viewMonthIndex = viewMonth.getMonth();

  const yearOptions = useMemo(() => {
    const minYear = minDate ? minDate.getFullYear() : new Date().getFullYear() - 100;
    const maxYear = maxDate ? maxDate.getFullYear() : new Date().getFullYear() - 18;
    const years = [];
    for (let y = maxYear; y >= minYear; y -= 1) years.push(y);
    return years;
  }, [minDate, maxDate]);

  const monthOptions = useMemo(() => {
    const months = [];
    for (let m = 0; m < 12; m += 1) {
      const monthStart = new Date(viewYear, m, 1);
      const monthEnd = new Date(viewYear, m + 1, 0);
      if (minDate && monthEnd < minDate) continue;
      if (maxDate && monthStart > maxDate) continue;
      months.push(m);
    }
    return months;
  }, [viewYear, minDate, maxDate]);

  const calendarDays = buildCalendarDays(viewMonth);
  const today = new Date();

  const setYear = (year) => {
    setViewMonth((prev) => clampViewMonth(year, prev.getMonth(), minDate, maxDate));
  };

  const setMonth = (month) => {
    setViewMonth((prev) => clampViewMonth(prev.getFullYear(), month, minDate, maxDate));
  };

  const canGoPrev = useMemo(() => {
    if (!minDate) return true;
    const prev = addMonths(viewMonth, -1);
    const endOfPrev = new Date(prev.getFullYear(), prev.getMonth() + 1, 0);
    return endOfPrev >= minDate;
  }, [viewMonth, minDate]);

  const canGoNext = useMemo(() => {
    if (!maxDate) return true;
    const next = addMonths(viewMonth, 1);
    return next <= maxDate;
  }, [viewMonth, maxDate]);

  const isDayDisabled = (day) => {
    const iso = toISODate(day);
    if (min && iso < min) return true;
    if (max && iso > max) return true;
    return false;
  };

  const handleSelect = (day) => {
    onChange(toISODate(day));
    onClose();
  };

  return (
    <Popover
      placement="bottom-start"
      strategy="fixed"
      gutter={10}
      isLazy
      lazyBehavior="unmount"
      isOpen={isOpen}
      onOpen={() => {
        setViewMonth(startOfMonth(selected || maxDate || new Date()));
        onOpen();
      }}
      onClose={onClose}
    >
      <PopoverTrigger>
        <Button
          type="button"
          w="100%"
          h="44px"
          px={3}
          borderRadius="lg"
          fontWeight="normal"
          fontSize="sm"
          bg={triggerBg}
          border="1px solid"
          borderColor={triggerBorder}
          color={selected ? triggerColor : placeholderColor}
          _hover={{ borderColor: triggerHoverBorder, bg: triggerBg }}
          _active={{ bg: triggerBg }}
          _focusVisible={{
            borderColor: "brand.400",
            boxShadow: "0 0 0 1px var(--chakra-colors-brand-400)",
          }}
        >
          <Flex align="center" gap={3} w="100%" minW={0}>
            <Flex
              align="center"
              justify="center"
              w="32px"
              h="32px"
              borderRadius="lg"
              bgGradient="linear(to-br, brand.500, brand.300)"
              color="gray.900"
              flexShrink={0}
            >
              <Calendar size={16} strokeWidth={2.25} />
            </Flex>
            <Text as="span" noOfLines={1} textAlign="left" flex={1}>
              {displayLabel || t("getStarted.dateOfBirthPlaceholder")}
            </Text>
          </Flex>
        </Button>
      </PopoverTrigger>

      <Portal>
        <PopoverContent
          w={{ base: "min(100vw - 32px, 320px)", sm: "320px" }}
          zIndex={2000}
          border="1px solid"
          borderColor={panelBorder}
          borderRadius="2xl"
          bg={panelBg}
          boxShadow={panelShadow}
          overflow="hidden"
          _focus={{ outline: "none" }}
          sx={{ backgroundColor: `${panelBg} !important` }}
        >
          <PopoverBody p={4} bg={panelBg}>
              <Flex align="center" justify="space-between" gap={1} mb={4}>
                <IconButton
                  type="button"
                  aria-label={t("getStarted.calendarPrevMonth")}
                  icon={<ChevronLeft size={18} />}
                  size="sm"
                  variant="ghost"
                  borderRadius="full"
                  flexShrink={0}
                  isDisabled={!canGoPrev}
                  onClick={() => setViewMonth((m) => addMonths(m, -1))}
                  _hover={{ bg: iconBtnHover }}
                />
                <Flex flex={1} gap={2} minW={0} justify="center">
                  <Select
                    aria-label={t("getStarted.calendarSelectMonth")}
                    size="sm"
                    flex={1}
                    minW={0}
                    value={String(viewMonthIndex)}
                    onChange={(e) => setMonth(Number(e.target.value))}
                    borderRadius="lg"
                    fontSize="sm"
                    fontWeight="semibold"
                    color={titleColor}
                    bg={selectBg}
                    borderColor={selectBorder}
                    _focus={selectFocus}
                    sx={nativeSelectOptionSx}
                  >
                    {monthOptions.map((m) => (
                      <option key={m} value={String(m)}>
                        {new Date(2000, m, 1).toLocaleDateString(locale, { month: "long" })}
                      </option>
                    ))}
                  </Select>
                  <Select
                    aria-label={t("getStarted.calendarSelectYear")}
                    size="sm"
                    w="88px"
                    flexShrink={0}
                    value={String(viewYear)}
                    onChange={(e) => setYear(Number(e.target.value))}
                    borderRadius="lg"
                    fontSize="sm"
                    fontWeight="semibold"
                    color={titleColor}
                    bg={selectBg}
                    borderColor={selectBorder}
                    _focus={selectFocus}
                    sx={nativeSelectOptionSx}
                  >
                    {yearOptions.map((y) => (
                      <option key={y} value={String(y)}>
                        {y}
                      </option>
                    ))}
                  </Select>
                </Flex>
                <IconButton
                  type="button"
                  aria-label={t("getStarted.calendarNextMonth")}
                  icon={<ChevronRight size={18} />}
                  size="sm"
                  variant="ghost"
                  borderRadius="full"
                  flexShrink={0}
                  isDisabled={!canGoNext}
                  onClick={() => setViewMonth((m) => addMonths(m, 1))}
                  _hover={{ bg: iconBtnHover }}
                />
              </Flex>

              <SimpleGrid columns={7} spacing={1} mb={1}>
                {weekdays.map((label) => (
                  <Text
                    key={label}
                    fontSize="10px"
                    fontWeight="semibold"
                    textAlign="center"
                    color={muted}
                    letterSpacing="0.04em"
                    py={1}
                  >
                    {label}
                  </Text>
                ))}
              </SimpleGrid>

              <SimpleGrid columns={7} spacing={1}>
                {calendarDays.map((day, idx) =>
                  day ? (
                    <Button
                      key={toISODate(day)}
                      type="button"
                      size="sm"
                      h="36px"
                      minW="36px"
                      p={0}
                      fontSize="sm"
                      fontWeight={selected && isSameDay(day, selected) ? "bold" : "medium"}
                      borderRadius="lg"
                      variant="unstyled"
                      isDisabled={isDayDisabled(day)}
                      color={
                        selected && isSameDay(day, selected)
                          ? "gray.900"
                          : isDayDisabled(day)
                            ? dayMuted
                            : titleColor
                      }
                      bg={
                        selected && isSameDay(day, selected)
                          ? "transparent"
                          : "transparent"
                      }
                      bgGradient={
                        selected && isSameDay(day, selected)
                          ? "linear(to-br, brand.500, brand.300)"
                          : undefined
                      }
                      boxShadow={
                        selected && isSameDay(day, selected)
                          ? "0 2px 10px rgba(52,211,153,0.3)"
                          : "none"
                      }
                      borderWidth={isSameDay(day, today) && !(selected && isSameDay(day, selected)) ? "1px" : "0"}
                      borderColor={todayRing}
                      _hover={
                        !isDayDisabled(day) && !(selected && isSameDay(day, selected))
                          ? { bg: dayHoverBg }
                          : undefined
                      }
                      _disabled={{ opacity: 0.35, cursor: "not-allowed" }}
                      onClick={() => handleSelect(day)}
                    >
                      {day.getDate()}
                    </Button>
                  ) : (
                    <Box key={`empty-${idx}`} h="36px" />
                  )
                )}
              </SimpleGrid>

              <Text fontSize="10px" color={muted} mt={3} textAlign="center" lineHeight="tall">
                {t("getStarted.dateOfBirthHint")}
              </Text>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default BirthDatePicker;
